import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormGroupDirective, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { U2bValidators } from '@shared/validators/validators';
import { Salutation } from '@shared/models/salutation';
import { Person } from '@shared/models/person';
import { debounceTime, distinctUntilChanged, map, skip, startWith, takeUntil, tap } from 'rxjs/operators';
import { Observable, Subject } from 'rxjs';
import { disableControlsByName } from '@shared/functions/form/disable-controls';
import { enableControlsByName } from '@shared/functions/form/enable-controls';
import { isFunction } from '@shared/functions/is-function';
import { DEFAULT_DEBOUNCE_TIME } from '@modules/bcm/@shared/constants';
import { isString } from '@shared/functions/is-string';
import { PersonApiService } from '@modules/bcm/@shared/services';
import { HttpParams } from '@angular/common/http';
import { MatFormFieldAppearance } from '@angular/material/form-field';
import { MatDialog } from '@angular/material/dialog';
import { PersonEditorDialogComponent } from './person-editor-dialog/person-editor-dialog.component';
import { TranslationService } from '@core/translation/translation.service';
import { TolgeeService } from '@core/translation/tolgee.service';
import { isUp2boatId, Up2boatIdSelectionService } from '@modules/bcm/@shared/services/up2boat-id-selection.service';

@Component({
    selector: 'form-widget-person',
    templateUrl: './form-widget-person.component.html',
    standalone: false
})
export class FormWidgetPersonComponent implements OnInit, OnDestroy {
    private _unsubscribeAll = new Subject<any>();

    @Input()
    appearance: MatFormFieldAppearance = 'outline';

    @Input()
    headline?: string;

    @Input()
    inputLabel?: string;

    @Input()
    person$: Observable<Person>;

    @Input()
    optionalFields: boolean;

    @Input()
    slimmedView = false;

    @Input()
    canCreate = true;

    @Input()
    personId: number;

    @Input()
    editable = false;

    @Input()
    disabled = false;

    @Output()
    personChanged: EventEmitter<Person> = new EventEmitter<Person>();

    personFormGroup: UntypedFormGroup;

    salutation = Salutation;

    person: Person;

    loadingPersons: boolean;

    persons: Person[];

    filteredPersons$: Observable<Person[]>;

    isSaving = false;

    constructor(
        private _formBuilder: UntypedFormBuilder,
        private formGroupDirective: FormGroupDirective,
        private _personApiService: PersonApiService,
        private _dialog: MatDialog,
        private _tolgee: TolgeeService,
        private up2boatIdSelection: Up2boatIdSelectionService
    ) {
    }

    private resolvingUp2boatId = false;

    ngOnInit(): void {

        if (!this.canCreate) {
            this.headline = this._tolgee.translate('workflows.formWidgets.person.headlineSelectOnly');
        } else if (this.headline === undefined || this.headline === null) {
            this.headline = this._tolgee.translate('workflows.formWidgets.person.headlineCreateHtml');
        }
        if (this.inputLabel === undefined || this.inputLabel === null) {
            this.inputLabel = this._tolgee.translate('workflows.formWidgets.person.searchLabel');
        }

        this._createForm();
        this._loadPersons();

        if (this.formGroupDirective?.form) {
            this.formGroupDirective.form.addControl('personForm', this.personFormGroup);
        }

        if (isFunction(this.person$?.subscribe)) {
            this.person$
                .pipe(
                    takeUntil(this._unsubscribeAll),
                    distinctUntilChanged((previous, current) => previous?.id === current?.id)
                )
                .subscribe(person => {
                    this.personFormGroup.patchValue({person, ...person});

                    if (person?.phones && person.phones.length) {
                        this.personFormGroup.get('phone').patchValue(person.phones[0]);
                    }

                    if (person?.id) {
                        disableControlsByName(
                            this.personFormGroup,
                            ['salutation', 'title', 'firstName', 'lastName', 'street', 'postCode', 'city', 'mail', 'phone', 'note']
                        );
                    }

                    this.personFormGroup.markAsDirty();
                });
        }
    }

    ngOnDestroy(): void {
        if (this.formGroupDirective?.form) {
            this.formGroupDirective.form.removeControl('personForm');
        }
        this._unsubscribeAll.next(undefined);
        this._unsubscribeAll.complete();
    }

    onClickRemovePerson(): void {
        this.person = null;
        this.personFormGroup.get('person').setValue(null);
        this.personFormGroup.reset();
        this.personFormGroup.enable();
    }

    openEditDialog(event: MouseEvent): void {
        event.preventDefault();
        event.stopPropagation();

        const disabled = this.personFormGroup.get('person').disabled;

        this._dialog.open(PersonEditorDialogComponent, {
            data: {
                parentFormGroup: this.formGroupDirective.form,
                personFormGroup: this.personFormGroup,
                person: this.person,
                optionalFields: this.optionalFields,
                appearance: this.appearance
            },
            disableClose: true,
        }).afterClosed().subscribe((person: Person) => {
            if (person) {
                const isExistingPerson = this.person?.id === person.id;
                if (isExistingPerson) {
                    this.personFormGroup.markAsPristine();
                    this.personFormGroup.markAsUntouched();
                    const existingIndex = this.persons.findIndex(p => p.id === person.id);
                    if (existingIndex !== -1) {
                        this.persons[existingIndex] = person;
                    }
                } else {
                    this.persons.unshift(person);
                }
                this.person = person;
                this.personFormGroup.get('person').setValue(person);
            }
            if (disabled) {
                this.personFormGroup.get('person').disable();
            }
        });
    }

    public displayPersonWith(person: Person): string {
        return person ? person.toDropdownString() : '';
    }

    private _createForm(): void {
        this.personFormGroup = this._formBuilder.group({
            person: [null],
            salutation: [
                null,
                this.optionalFields ? [] : [U2bValidators.required(TranslationService.translate('common.validationMsg.salutationRequired'))],
            ],
            title: [null],
            firstName: [
                null,
                this.optionalFields ? [] : [U2bValidators.required(TranslationService.translate('common.validationMsg.firstNamesRequired'))],
            ],
            lastName: [
                null,
                this.optionalFields ? [] : [U2bValidators.required(TranslationService.translate('common.validationMsg.lastNamesRequired'))],
            ],
            street: [
                null,
                this.optionalFields ? [] : [U2bValidators.required(TranslationService.translate('common.validationMsg.streetRequired'))]
            ],
            postCode: [
                null,
                this.optionalFields ? [] : [U2bValidators.required(TranslationService.translate('common.validationMsg.postalCodeRequired'))]
            ],
            city: [
                null,
                this.optionalFields ? [] : [U2bValidators.required(TranslationService.translate('common.validationMsg.cityRequired'))]
            ],
            mail: [
                null,
                [U2bValidators.email(TranslationService.translate('common.validationMsg.emailFormatCheckRequired'), true)]
            ],
            phone: this._formBuilder.group({
                type: [null],
                number: [null],
            }),
            note: [null, [U2bValidators.maxLength(1024)]]
        });

        if (!this.canCreate && !this.editable) {
            disableControlsByName(
                this.personFormGroup,
                ['salutation', 'title', 'firstName', 'lastName', 'street', 'postCode', 'city', 'mail', 'phone', 'note']
            );
        }


        this.personFormGroup
            .get('person')
            .valueChanges
            .pipe(
                takeUntil(this._unsubscribeAll),
                distinctUntilChanged((previous, current) => previous?.id === current?.id)
            )
            .subscribe((person) => {

                this.person = person;
                this.personChanged.emit(person);

                if (person && person.id) {
                    disableControlsByName(
                        this.personFormGroup,
                        ['salutation', 'title', 'firstName', 'lastName', 'street', 'postCode', 'city', 'mail', 'phone', 'note']
                    );
                } else {
                    enableControlsByName(
                        this.personFormGroup,
                        ['salutation', 'title', 'firstName', 'lastName', 'street', 'postCode', 'city', 'mail', 'phone', 'note'],
                        true
                    );
                }
            });

        if (this.disabled) {
            this.personFormGroup.disable({ emitEvent: false });
        }

    }

    private _loadPersons(): void {
        this.persons = [];
        this.loadingPersons = true;
        this._personApiService
            .getAll(new HttpParams().set('includeBoats', 'true'))
            .pipe(
                takeUntil(this._unsubscribeAll),
                tap(() => this.loadingPersons = false)
            )
            .subscribe(persons => {
                this.persons = persons;
                if (this.personId) {
                    this.person = persons.find(person => person.id === this.personId);
                    this.personFormGroup.patchValue({person: this.person});
                }
            });

        this.filteredPersons$ = this.personFormGroup.get('person').valueChanges
            .pipe(
                startWith(''),
                debounceTime(DEFAULT_DEBOUNCE_TIME),
                map((value) => isString(value) ? Person.filterPersons(this.persons, value) : this.persons)
            );

        this.personFormGroup.get('person').valueChanges
            .pipe(
                takeUntil(this._unsubscribeAll),
                debounceTime(DEFAULT_DEBOUNCE_TIME),
                distinctUntilChanged()
            )
            .subscribe(value => {
                if (isString(value) && isUp2boatId(value)) {
                    void this.applyUp2boatPerson(value);
                }
            });
    }

    private async applyUp2boatPerson(code: string): Promise<void> {
        if (this.resolvingUp2boatId) {
            return;
        }

        this.resolvingUp2boatId = true;

        try {
            // Keine bekannte Up2Boat-ID: nichts tun, die lokale Namensfilterung greift weiter.
            const resolution = await this.up2boatIdSelection.resolvePerson(code);

            if (resolution.found && resolution.value) {
                this.personFormGroup.get('person').setValue(resolution.value);
            }
        } finally {
            this.resolvingUp2boatId = false;
        }
    }
}
